import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type { CommandResponseFE, VehicleCommandInputFE } from 'domain/command';
import { commandFromRestV1 } from 'domain/mappers/commandFromRestV1';

import type { ServiceCommandError } from './commandFeedback';
import { normaliseCommandError } from './commandFeedback';
import { errorHandler, formatAxiosError, isAxiosError } from './errors';
import { commandsRestApi, correlationHeaders } from './restClient';

const COMMAND_ERROR_MESSAGE = 'Unexpected error occurred while triggering the command';

/**
 * Failures are RETURNED as a {@link ServiceCommandError}, not thrown: RTK Query would flatten a
 * thrown error to name/message/stack and the lost HTTP status would collapse it to the generic row.
 */
const sendCommandFn = async (
    variables: VehicleCommandInputFE
): Promise<{ data: CommandResponseFE } | { error: ServiceCommandError }> => {
    try {
        const response = await commandsRestApi.post<unknown>(
            '/api/v1/commands',
            { vin: variables.vin, command: variables.command },
            correlationHeaders()
        );
        const data = commandFromRestV1(response.data);

        if (!data.isSuccess) {
            return {
                error: {
                    status: undefined,
                    code: undefined,
                    source: 'UNKNOWN',
                    message: data.errorMessage ?? 'Unknown command error',
                } satisfies ServiceCommandError,
            };
        }

        return { data };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        errorHandler(COMMAND_ERROR_MESSAGE, loggableError);
        return { error: normaliseCommandError(error, COMMAND_ERROR_MESSAGE) };
    }
};

export const commandApi = createApi({
    reducerPath: 'command',
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        sendCommand: builder.mutation<CommandResponseFE, VehicleCommandInputFE>({
            queryFn: sendCommandFn,
        }),
    }),
});

export const { useSendCommandMutation } = commandApi;
